import "../../../chunk-D57E6H3M.mjs";

// src/components/composite/Conversations/EmailThread.tsx
import { useId, useState } from "react";
import { MdExpandMore } from "react-icons/md";
import { useNow } from "../../../hooks/useNow.mjs";
import { formatTimestamp } from "../../../utils/formatTimestamp.mjs";
import { getInitials } from "../../../utils/getInitials.mjs";
import { Avatar, Button, CopyButton } from "../../primitive/index.mjs";
import { MessageAttachments } from "./MessageAttachments.mjs";
import { useConversationsTranslation } from "./use-conversations-translation.mjs";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function mailboxLabel({ address, name }) {
  return name ? `${name} <${address}>` : address;
}
function EmailAddress({ label, copyLabel, mailbox }) {
  return /* @__PURE__ */ jsxs("div", { className: "email-address-row", children: [
    /* @__PURE__ */ jsx("dt", { children: label }),
    /* @__PURE__ */ jsxs("dd", { children: [
      /* @__PURE__ */ jsx("span", { className: "email-address-value", dir: "auto", children: mailboxLabel(mailbox) }),
      /* @__PURE__ */ jsx(CopyButton, { variant: "borderless", size: "xs", value: mailbox.address, "aria-label": copyLabel })
    ] })
  ] });
}
EmailAddress.displayName = "EmailAddress";
function EmailMessage({ message, name, initiallyOpen, storeAvatarUrl }) {
  const { t, i18n } = useConversationsTranslation();
  const stamp = formatTimestamp(message.createdAt, { now: useNow(), locale: i18n.resolvedLanguage || i18n.language, time: true });
  const [open, setOpen] = useState(initiallyOpen);
  const [showHtml, setShowHtml] = useState(false);
  const bodyId = useId();
  const email = message.email;
  const isMerchantReply = message.role === "assistant" && message.sentBy === "human";
  const from = email?.fromAddress ? { address: email.fromAddress, name: email.fromName } : void 0;
  const to = email?.to ?? [];
  const cc = email?.cc ?? [];
  const replyTo = email?.replyTo && email.replyTo.address !== email.fromAddress ? email.replyTo : void 0;
  const sender = email?.fromName || email?.fromAddress || (message.role === "user" ? name : message.sentBy === "human" ? t("shared.chat.log.sent_by_you") : t("shared.chat.log.sent_by_system"));
  return /* @__PURE__ */ jsxs("article", { className: "email-message", "data-merchant-reply": isMerchantReply || void 0, children: [
    /* @__PURE__ */ jsxs("button", { type: "button", className: "email-message-toggle", "aria-expanded": open, "aria-controls": bodyId, onClick: () => setOpen(!open), children: [
      /* @__PURE__ */ jsx(Avatar, { className: "email-avatar", tone: "neutral", fit: "contain", src: isMerchantReply ? storeAvatarUrl?.trim() || void 0 : void 0, alt: "", fallback: getInitials(sender, i18n.resolvedLanguage || i18n.language), "aria-hidden": "true" }),
      /* @__PURE__ */ jsxs("span", { className: "email-message-summary", children: [
        /* @__PURE__ */ jsxs("span", { className: "email-message-topline", children: [
          /* @__PURE__ */ jsx("span", { className: "email-message-sender", dir: "auto", children: sender }),
          stamp && /* @__PURE__ */ jsx("time", { dateTime: stamp.dateTime, title: stamp.title, children: stamp.text })
        ] }),
        !open && /* @__PURE__ */ jsx("span", { className: "email-message-preview", dir: "auto", children: message.content })
      ] }),
      /* @__PURE__ */ jsx(MdExpandMore, { className: "email-chevron", "aria-hidden": "true" })
    ] }),
    /* @__PURE__ */ jsx("div", { id: bodyId, hidden: !open, className: "email-message-body", children: open && /* @__PURE__ */ jsxs(Fragment, { children: [
      (from || to.length > 0) && /* @__PURE__ */ jsxs("details", { className: "email-addressing", children: [
        /* @__PURE__ */ jsxs("summary", { children: [
          /* @__PURE__ */ jsx("span", { children: to.length > 0 ? t("shared.chat.email.to_address", { address: to.map(mailboxLabel).join(", ") }) : t("shared.chat.email.addresses") }),
          /* @__PURE__ */ jsxs("span", { className: "email-details-label", children: [
            t("shared.chat.email.details"),
            " ",
            /* @__PURE__ */ jsx(MdExpandMore, { "aria-hidden": "true" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("dl", { className: "email-address-list", children: [
          email?.subject && /* @__PURE__ */ jsxs("div", { className: "email-address-row", children: [
            /* @__PURE__ */ jsx("dt", { children: t("shared.chat.email.subject") }),
            /* @__PURE__ */ jsx("dd", { children: /* @__PURE__ */ jsx("span", { className: "email-address-value", dir: "auto", children: email.subject }) })
          ] }),
          from && /* @__PURE__ */ jsx(EmailAddress, { label: t("shared.chat.email.from"), copyLabel: t("shared.chat.email.copy_from", { address: from.address }), mailbox: from }),
          replyTo && /* @__PURE__ */ jsx(EmailAddress, { label: t("shared.chat.email.reply_to"), copyLabel: t("shared.chat.email.copy_reply_to", { address: replyTo.address }), mailbox: replyTo }),
          to.map((mailbox) => /* @__PURE__ */ jsx(EmailAddress, { label: t("shared.chat.email.to"), copyLabel: t("shared.chat.email.copy_to", { address: mailbox.address }), mailbox }, `to-${mailbox.address}`)),
          cc.map((mailbox) => /* @__PURE__ */ jsx(EmailAddress, { label: t("shared.chat.email.cc"), copyLabel: t("shared.chat.email.copy_cc", { address: mailbox.address }), mailbox }, `cc-${mailbox.address}`))
        ] })
      ] }),
      email?.htmlTooLarge && /* @__PURE__ */ jsx("p", { className: "email-html-dropped", children: t("shared.chat.email.html_too_large") }),
      email?.html && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "borderless", color: "neutral", onClick: () => setShowHtml(!showHtml), children: showHtml ? t("shared.chat.email.show_plain_text") : t("shared.chat.email.show_html") }),
      showHtml && email?.html ? /* @__PURE__ */ jsx(
        "iframe",
        {
          title: t("shared.chat.email.html_title", { sender }),
          sandbox: "",
          referrerPolicy: "no-referrer",
          srcDoc: `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:">${email.html}`,
          className: "email-html-body"
        }
      ) : /* @__PURE__ */ jsx("p", { className: "email-message-text", dir: "auto", children: message.content }),
      email?.quoted && /* @__PURE__ */ jsxs("details", { className: "email-history", children: [
        /* @__PURE__ */ jsx("summary", { children: t("shared.chat.email.quoted_history") }),
        /* @__PURE__ */ jsx("pre", { className: "email-history-body", dir: "auto", children: email.quoted })
      ] }),
      !!message.attachments?.length && /* @__PURE__ */ jsx(MessageAttachments, { attachments: message.attachments })
    ] }) })
  ] });
}
function EmailThread({ session, name, storeAvatarUrl }) {
  const { t } = useConversationsTranslation();
  const subject = session.subject || t("shared.chat.email.untitled");
  return /* @__PURE__ */ jsxs("section", { className: "email-thread", "aria-label": subject, children: [
    /* @__PURE__ */ jsxs("header", { className: "email-thread-heading", children: [
      /* @__PURE__ */ jsx("h2", { dir: "auto", children: subject }),
      /* @__PURE__ */ jsx("span", { className: "bs-conversations__meta", children: t("shared.chat.content.message_count", { count: session.chatMessage.length }) })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "email-messages", children: session.chatMessage.map((message, index) => /* @__PURE__ */ jsx(EmailMessage, { message, name, storeAvatarUrl, initiallyOpen: index === session.chatMessage.length - 1 }, message.id)) })
  ] });
}
EmailThread.displayName = "EmailThread";
export {
  EmailThread
};
//# sourceMappingURL=EmailThread.mjs.map